import { type FunctionComponent, type PropsWithChildren, createElement, Fragment } from 'react'
import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { partnerPickDevicesRouter } from './routing/partnerPickDevicesRouter'
import { partnerPickDevicesPartnerScopedRouter } from './routing/partnerPickDevicesPartnerScopedRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import {
  partnerPickDevicesLegacyNavAccessControl,
  partnerPickDevicesPartnerScopedAccessControl,
} from './access-control/partnerPickDevicesPartnerScopedAccess.configuration'
import { equipmentDataAccessControl } from './access-control/equipmentDataAccess.configuration'
import { Permission } from './access-control/permissions'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import { useIsPartnerBulkDeliveryEnabled } from './utils/domain/useIsPartnerBulkDeliveryEnabled'

// NavItemWrapper that only renders for NON-partner-bulk-delivery locations
const NonPartnerBulkDeliveryNavItemWrapper: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const isPartnerBulkDelivery = useIsPartnerBulkDeliveryEnabled()

  if (isPartnerBulkDelivery) return null

  return createElement(Fragment, null, children)
}

// NavItemWrapper that only renders for partner bulk delivery locations
const PartnerBulkDeliveryNavItemWrapper: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const isPartnerBulkDelivery = useIsPartnerBulkDeliveryEnabled()

  if (!isPartnerBulkDelivery) return null

  return createElement(Fragment, null, children)
}

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'partner-pick-devices',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'partner-pick-devices-dashboard-tool',
  },
  routes,
  translations: {},
  lazyTranslations: {
    EN_US: () => import('./translations/en-US.json'),
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [partnerPickDevicesRouter],
    partnerScoped: [partnerPickDevicesPartnerScopedRouter],
  },
  navigationEntries: [
    {
      attachToV3: NavEntryPointV3.Fulfillment,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Manage pick devices across all banners the user can access under the current partner',
          labelId: 'partnerPickDevicesDomain.title.new',
          route: 'partner-pick-devices-partner-scoped-list',
          accessControl: partnerPickDevicesPartnerScopedAccessControl,
          position: 3,
          positionNavV3: 2.5,
          NavItemWrapper: NonPartnerBulkDeliveryNavItemWrapper,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Fulfillment,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Manage pick devices for warehouse staff and shoppers',
          labelId: 'partnerPickDevicesDomain.title.new',
          route: 'partner-pick-devices-list',
          accessControl: partnerPickDevicesLegacyNavAccessControl,
          position: 3,
          positionNavV3: 3,
          NavItemWrapper: NonPartnerBulkDeliveryNavItemWrapper,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.Fulfillment,
      subNavItems: [
        {
          type: 'item',
          llmDescription:
            'Manage equipment data for partner bulk delivery warehouse staff and shoppers',
          labelId: 'partnerPickDevicesDomain.title.equipmentData',
          route: 'partner-pick-devices-list',
          accessControl: equipmentDataAccessControl,
          position: 3,
          positionNavV3: 3,
          NavItemWrapper: PartnerBulkDeliveryNavItemWrapper,
          inToolPermissions: [
            {
              slug: Permission.PartnerPickDevicesManageShoppers,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.PartnerPickDevicesManageShoppers,
            },
            {
              slug: Permission.PartnerBulkDeliveryVehiclesView,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.PartnerBulkDeliveryVehiclesView,
            },
            {
              slug: Permission.PartnerBulkDeliveryVehiclesModify,
              permissionLabelId:
                'permissionAccordion.permissions.' + Permission.PartnerBulkDeliveryVehiclesModify,
            },
          ],
        },
      ],
    },
  ],
  adminNavigationEntries: [],
}

export default definition
